import { createAsyncThunk } from '@reduxjs/toolkit'
import callAPI from 'api'
import { Fail, Success } from 'store/types'
import { Department, FixMeAny, Master } from 'types/global'
import {
  MasterPayload,
  MasterDetailPayload,
  MasterResponse,
  UpdateMasterDetailsPayload,
  TranslationResponse,
  GetMasterBranchesResponse,
} from './types/master'
import { fileToBase64 } from 'utils/file'

/**
 * Async function to get master
 * Returns data directly
 */
export const getMaster = async (payload: MasterPayload) => {
  const response = await callAPI('crm_get_master', payload)

  if (response.status === 'SUCCESS') {
    const { data } = response

    const master: Master = {
      id: data.master_id,
      name: data.master_name,
      shortName: data.master_short_name,
      countryCode: data.country_code,
    }

    return master
  }
}

export const getCurrentMasterDetails = async () =>
  await callAPI<MasterResponse, MasterDetailPayload>('crm_get_master_details', [], {
    requireAuthorization: true,
  })

export const getMasterDetailsApi = async (payload: MasterDetailPayload) =>
  await callAPI<MasterResponse, MasterDetailPayload>('crm_get_master_details', payload, {
    requireAuthorization: true,
  })

/**
 * Async thunk to fetch master
 */
export const getMasterDetails = createAsyncThunk<Fail | Success<MasterResponse>, MasterDetailPayload>(
  'master/getMaster',
  getMasterDetailsApi,
)

/**
 * Async function to get branches for selected Master
 * Returns data directly
 */
export const getMasterBranches = async () =>
  await callAPI<GetMasterBranchesResponse>('crm_get_master_branches', {}, { requireAuthorization: true })

/**
 * Async function to get departments for selected Master
 * Returns data directly
 */
export const getDepartments = async () => {
  const response = await callAPI('crm_get_user_department', {}, { requireAuthorization: true })

  if (response.status === 'SUCCESS') {
    const department: Department[] = response.data.map((d: FixMeAny) => ({
      departmentId: d.department_id,
      departmentName: d.department_name,
    }))
    return department
  }
}

export const updateMasterDetails = async (payload: UpdateMasterDetailsPayload) =>
  await callAPI<MasterResponse, UpdateMasterDetailsPayload>('crm_update_master_details', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const deleteCompanyLogo = (payload: { logo_filename: string }) =>
  callAPI<{ success: true }, { logo_filename: string }>('crm_delete_master_logo', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const uploadMasterLogo = async (payload: File) => {
  const converted = await fileToBase64(payload)
  return callAPI<{ photo_filename: string; photo_url: string }, { file: string }>(
    'crm_upload_master_logo',
    [{ file: String(converted) }],
    { requireAuthorization: true },
  )
}

/**
 * Async function to get translations
 */
export const getFullTranslation = async (lang?: string) =>
  await callAPI<TranslationResponse, { language_code?: string }>(
    'crm_get_master_translations',
    [{ language_code: lang }],
    { requireAuthorization: true },
  )

/**
 * Async function to get translations
 */
export const getCommonTranslation = async (lang?: string) =>
  await callAPI<TranslationResponse, { language_code?: string }>('crm_get_common_translations', [
    { language_code: lang },
  ])
